import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import {
  Plus,
  Search,
  Filter,
  ArrowUpDown,
  ChevronDown,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { StatusBadge } from '@/components/StatusBadge'
import { Pagination } from '@/components/Pagination'
import { TransitionDialog } from '@/components/TransitionDialog'
import { useLeads, useTransitionLead } from '@/hooks/useLeads'
import { useUsers } from '@/hooks/useUsers'
import { LeadStatus, SourceChannel, LeadFilterParams } from '@/types/api'

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: LeadStatus.NEW, label: 'New' },
  { value: LeadStatus.CONTACTED, label: 'Contacted' },
  { value: LeadStatus.ESTIMATE_SCHEDULED, label: 'Estimate Scheduled' },
  { value: LeadStatus.ESTIMATE_SENT, label: 'Estimate Sent' },
  { value: LeadStatus.WON, label: 'Won' },
  { value: LeadStatus.LOST, label: 'Lost' },
]

const sourceOptions = [
  { value: '', label: 'All Sources' },
  { value: SourceChannel.PHONE, label: 'Phone' },
  { value: SourceChannel.WALK_IN, label: 'Walk-in' },
  { value: SourceChannel.WEBSITE, label: 'Website' },
  { value: SourceChannel.REFERRAL, label: 'Referral' },
  { value: SourceChannel.GOOGLE_ADS, label: 'Google Ads' },
  { value: SourceChannel.FACEBOOK, label: 'Facebook' },
  { value: SourceChannel.INSTAGRAM, label: 'Instagram' },
  { value: SourceChannel.YELP, label: 'Yelp' },
  { value: SourceChannel.RECURRING_CLIENT, label: 'Recurring Client' },
  { value: SourceChannel.OTHER, label: 'Other' },
]

const transitionTargets: Record<LeadStatus, LeadStatus[]> = {
  [LeadStatus.NEW]: [LeadStatus.CONTACTED, LeadStatus.LOST],
  [LeadStatus.CONTACTED]: [LeadStatus.ESTIMATE_SCHEDULED, LeadStatus.LOST],
  [LeadStatus.ESTIMATE_SCHEDULED]: [LeadStatus.ESTIMATE_SENT, LeadStatus.LOST],
  [LeadStatus.ESTIMATE_SENT]: [LeadStatus.WON, LeadStatus.LOST],
  [LeadStatus.WON]: [],
  [LeadStatus.LOST]: [],
  [LeadStatus.QUALIFIED]: [],
}

export function LeadsPage() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<LeadFilterParams>({
    limit: 25,
    offset: 0,
    sort_by: 'updated_at',
    sort_order: 'desc',
  })
  const [searchTerm, setSearchTerm] = useState('')

  const { data: leads, isLoading } = useLeads({
    ...filters,
    search: searchTerm || undefined,
  })
  const { data: users } = useUsers()

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilters((prev) => ({ ...prev, offset: 0 }))
  }

  const handleFilterChange = (key: keyof LeadFilterParams, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
      offset: 0,
    }))
  }

  const handleSort = (column: string) => {
    setFilters((prev) => ({
      ...prev,
      sort_by: column as LeadFilterParams['sort_by'],
      sort_order:
        prev.sort_by === column && prev.sort_order === 'asc' ? 'desc' : 'asc',
    }))
  }

  const getSortIcon = (column: string) => {
    if (filters.sort_by !== column) return <ArrowUpDown className="h-4 w-4" />
    return (
      <ChevronDown
        className={`h-4 w-4 transition-transform ${
          filters.sort_order === 'asc' ? 'rotate-180' : ''
        }`}
      />
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Leads</h1>
        <Button onClick={() => navigate('/leads/new')}>
          <Plus className="mr-2 h-4 w-4" />
          New Lead
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <form onSubmit={handleSearch} className="relative lg:col-span-2">
              <Search className="absolute left-1 top-1/2 -translate-y-1/2 h-2 w-2 text-muted-foreground pointer-events-none z-10" />
              <Input
                placeholder="Search by name, phone, address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </form>

            <Select
              value={filters.lead_status || ''}
              onValueChange={(v) => handleFilterChange('lead_status', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((opt) => (
                  <SelectItem key={opt.value || 'all'} value={opt.value || 'all'}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.source_channel || ''}
              onValueChange={(v) => handleFilterChange('source_channel', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                {sourceOptions.map((opt) => (
                  <SelectItem key={opt.value || 'all'} value={opt.value || 'all'}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.assigned_to || ''}
              onValueChange={(v) => handleFilterChange('assigned_to', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Assignees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assignees</SelectItem>
                {users?.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('last_name')}
                    >
                      <div className="flex items-center gap-1">
                        Name
                        {getSortIcon('last_name')}
                      </div>
                    </TableHead>
                    <TableHead className="hidden md:table-cell">Phone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden md:table-cell">Source</TableHead>
                    <TableHead
                      className="hidden md:table-cell cursor-pointer"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center gap-1">
                        Created
                        {getSortIcon('created_at')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="hidden md:table-cell cursor-pointer"
                      onClick={() => handleSort('updated_at')}
                    >
                      <div className="flex items-center gap-1">
                        Updated
                        {getSortIcon('updated_at')}
                      </div>
                    </TableHead>
                    <TableHead className="md:hidden">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads?.items.map((lead) => (
                    <LeadRow
                      key={lead.id}
                      lead={lead}
                      onClick={() => navigate(`/leads/${lead.id}`)}
                    />
                  ))}
                  {(!leads?.items || leads.items.length === 0) && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center text-muted-foreground"
                      >
                        No leads found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <Pagination
                total={leads?.total || 0}
                limit={filters.limit || 25}
                offset={filters.offset || 0}
                onPageChange={(offset) =>
                  setFilters((prev) => ({ ...prev, offset }))
                }
                onLimitChange={(limit) =>
                  setFilters((prev) => ({ ...prev, limit, offset: 0 }))
                }
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

interface LeadRowProps {
  lead: {
    id: string
    first_name: string
    last_name: string
    phone?: string
    lead_status: LeadStatus
    source_channel: SourceChannel
    created_at: string
    updated_at: string
  }
  onClick: () => void
}

function LeadRow({ lead, onClick }: LeadRowProps) {
  const transitionMutation = useTransitionLead(lead.id)
  const availableTransitions = transitionTargets[lead.lead_status] || []
  const [showWonDialog, setShowWonDialog] = useState(false)

  const handleTransition = (e: React.MouseEvent, newStatus: LeadStatus) => {
    e.stopPropagation()
    if (newStatus === LeadStatus.WON) {
      setShowWonDialog(true)
    } else if (newStatus === LeadStatus.LOST) {
      transitionMutation.mutate({
        new_status: newStatus,
        disqualification_reason: 'Marked as lost from list view',
      })
    } else {
      transitionMutation.mutate({ new_status: newStatus })
    }
  }

  return (
    <>
    <TableRow className="cursor-pointer" onClick={onClick}>
      <TableCell className="font-medium">
        {lead.first_name} {lead.last_name}
      </TableCell>
      <TableCell className="hidden md:table-cell">{lead.phone || '-'}</TableCell>
      <TableCell>
        <StatusBadge status={lead.lead_status} />
      </TableCell>
      <TableCell className="hidden md:table-cell capitalize">
        {lead.source_channel.replace('_', ' ')}
      </TableCell>
      <TableCell className="hidden md:table-cell text-muted-foreground">
        {format(new Date(lead.created_at), 'MMM d, yyyy')}
      </TableCell>
      <TableCell className="hidden md:table-cell text-muted-foreground">
        {format(new Date(lead.updated_at), 'MMM d, yyyy')}
      </TableCell>
      <TableCell className="md:hidden">
        {availableTransitions.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="outline" size="sm">
                Move to
                <ChevronDown className="ml-1 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {availableTransitions.map((status) => (
                <DropdownMenuItem
                  key={status}
                  onClick={(e) => handleTransition(e, status)}
                >
                  {statusOptions.find((o) => o.value === status)?.label || status}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>

    {/* Won transition (project size picker) */}
    <TransitionDialog
      open={showWonDialog}
      onOpenChange={setShowWonDialog}
      mode="won"
      isPending={transitionMutation.isPending}
      onConfirm={({ projectSize }) =>
        transitionMutation.mutate({
          new_status: LeadStatus.WON,
          project_size: projectSize,
        })
      }
    />
    </>
  )
}
